import { faCircle1, faCircle2 } from '@fortawesome/pro-light-svg-icons';
import { Stack, styled, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router';
import { FullscreenLoadingSpinner } from 'src/components/Loading/FullscreenLoadingSpinner';
import SearchableDropdown from 'src/components/SearchableDropdown';
import { StepContent } from 'src/components/StepElements';
import { Toast } from 'src/components/Toast';
import { FormFooter } from 'src/pages/CreateNewProductPage/CreateProductForm/CreateProductFormFooter';
import { useTenant } from 'src/providers/Tenant';
import { AttributeNames, getConfiguration, patchConfiguration } from 'src/services/productInventory';

export default function SelectPartNumber() {
  const location = useLocation();
  const { accountId } = useTenant();
  const { t } = useTranslation('createProduct');
  const history = useHistory();
  const searchParams = new URLSearchParams(location?.search);
  const configurationPID = searchParams.get('configurationPID');
  const productName = searchParams.get('productName');
  const runID = searchParams.get('runID');

  const {
    data: response,
    isLoading: isLoadingAttributes,
    isError: isErrorAttributes,
  } = useQuery(['attributes'], () => getConfiguration(configurationPID!, accountId));

  const {
    mutate: mutateAttributeNames,
    isLoading: isLoadingSettingAttributeNames,
    isError: isErrorSettingAttributeNames,
  } = useMutation(
    ['mutateAttributeNames'],
    async () => {
      /* istanbul ignore next */
      if (!selectedPartNameAttributeName || !selectedPartNumberAttributeName || !configurationPID || !runID) {
        return;
      }
      const body: AttributeNames = {
        partNameDesignAttribute: selectedPartNameAttributeName,
        partNumberDesignAttribute: selectedPartNumberAttributeName,
        cadJobRunID: runID,
      };

      await patchConfiguration(configurationPID, body, accountId);
    },
    {
      onSuccess: () => {
        history.push(
          `/create-product/sales-bom?runID=${runID}&configurationPID=${configurationPID}&productName=${productName}`
        );
      },
    }
  );

  const [selectedPartNumberAttributeName, setSelectedPartNumberAttributeName] = useState<string | null>(null);
  const [selectedPartNameAttributeName, setSelectedPartNameAttributeName] = useState<string | null>(null);

  const attributesData = response?.hierarchyDesignAttributesPreview;
  const attributeNames = attributesData?.map((attr) => attr.name) || [];

  const tableHeight =
    attributesData?.reduce((acc, curr) => (curr.values.length > acc ? curr.values.length : acc), 0) ?? 0;

  const haveBeenNamesBothSelected = Boolean(selectedPartNumberAttributeName && selectedPartNameAttributeName);

  if (isLoadingAttributes) {
    return <FullscreenLoadingSpinner />;
  }

  return (
    <StepContent>
      <Stack direction="column" spacing={2} useFlexGap>
        <Border>
          {attributesData && attributeNames.length ? (
            <Table data-testid="table">
              <thead>
                <tr>
                  {attributeNames.map((name) => (
                    <th key={name}>{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {new Array(tableHeight).fill(undefined).map((_, i) => (
                  <tr key={`table-row-${attributesData[i]?.name}`}>
                    {attributesData?.map((attr, j) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <td key={`table-cell-${i}-${j}`}>{attr.values[i]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            /* istanbul ignore next */
            <NoAttributes sx={{ margin: 'auto', width: 'fit-content' }}>No attributes found</NoAttributes>
          )}
        </Border>
        <form>
          <Title>
            <Trans i18nKey="createProduct:dropDownInstructions" />
          </Title>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            useFlexGap
            spacing={2}
            flexWrap="wrap"
            justifyContent="space-evenly"
          >
            <Stack direction="column" useFlexGap spacing={2} flexWrap="wrap" justifyContent="space-evenly">
              <SearchableDropdown
                value={selectedPartNumberAttributeName}
                setValue={setSelectedPartNumberAttributeName}
                options={attributeNames}
                sx={{ marginLeft: '2.5rem' }}
                placeholder={t('selectPartNumberColumn')}
                label={t('selectPartNumberLabel')}
                startIcon={faCircle1}
              />
            </Stack>
            <Stack direction="column" useFlexGap spacing={2} flexWrap="wrap" justifyContent="space-evenly">
              <SearchableDropdown
                value={selectedPartNameAttributeName}
                setValue={setSelectedPartNameAttributeName}
                options={attributeNames}
                sx={{ marginLeft: '2.5rem' }}
                placeholder={t('selectPartNameColumn')}
                label={t('selectPartNameLabel')}
                startIcon={faCircle2}
              />
            </Stack>
          </Stack>
        </form>
        <Toast
          dependency={isErrorAttributes}
          severity="error"
          title={t('errorGettingAttributesTitle')}
          message={t('errorGettingAttributesMessage') || /* istanbul ignore next */ ''}
        />
        <Toast
          dependency={isErrorSettingAttributeNames}
          severity="error"
          title={t('errorSettingAttributesTitle')}
          message={t('errorSettingAttributesMessage') || /* istanbul ignore next */ ''}
        />
      </Stack>
      <FormFooter
        buttonDisabled={!haveBeenNamesBothSelected || isLoadingSettingAttributeNames}
        buttonI18nText="nextStep"
        isLoading={isLoadingSettingAttributeNames}
        backButton={false}
        nextOnClick={() => mutateAttributeNames()}
      />
    </StepContent>
  );
}

const NoAttributes = styled(Typography)`
  margin: auto;
  line-height: 200px;
  height: 200px;
`;

const Title = styled(Typography)`
  margin: 1rem;
`;

const Border = styled('div')`
  width: 100%;
  display: flex;
  min-height: 280px;
  max-height: 400px;
  border-radius: 3px;
  border: 1px solid ${({ theme }) => theme.palette.grey[500]};
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.palette.grey[600]} white;
`;

const Table = styled('table')`
  width: 100%;
  border-spacing: 0;

  th {
    max-width: fit-content;
    border: 1px solid ${({ theme }) => theme.palette.grey[300]};
    border-left: none;
    border-top: none;
    text-align: left;
    padding: 0 ${({ theme }) => theme.spacing(2)};
  }
  th:last-child {
    border-right: none;
  }
  td {
    border-right: 1px solid ${({ theme }) => theme.palette.grey[300]};
    padding: 0 ${({ theme }) => theme.spacing(2)};
  }
  td:last-child {
    border-left: none;
    border-right: none;
  }
`;
